import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  viewChild,
} from '@angular/core';

import type { Monaco } from '@monaco-editor/loader';
import loader from '@monaco-editor/loader';
import type { editor as MonacoEditor, Uri } from 'monaco-editor';

import { translated } from '../../utils/translated.util';

/**
 * Monaco-backed code editor. Editable and validating by default (schema editing); with
 * `readOnly` and `[validate]="false"` it is a plain viewer for server payloads: no JSON error
 * marks or footer, Tab leaves the editor, and a `<pre>` shows the same text if Monaco cannot load.
 */
@Component({
  selector: 'nf-monaco-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Code and JSON must never be rewritten by the browser's page translation.
  host: { translate: 'no' },
  template: `
    <div
      class="relative border rounded-md overflow-hidden"
      [class.border-danger-500]="hasError()"
      [class.border-sk-border-light]="!hasError()"
      [style.height]="height()">
      <div #editorContainer class="w-full h-full" [class.hidden]="loadFailed()"></div>
      @if (loading() && !loadFailed()) {
        <div class="absolute inset-0 animate-pulse bg-sk-surface-raised" aria-hidden="true"></div>
      }
      @if (loadFailed()) {
        <pre
          class="w-full h-full m-0 overflow-auto p-2 text-xs font-mono whitespace-pre text-sk-text"
          tabindex="0"
          >{{ value() }}</pre>
      }
    </div>
    @if (validate() && hasError()) {
      <p class="mt-1 text-xs text-danger-600 flex items-center gap-1">
        <span>⚠</span> {{ errorsHint() }}
      </p>
    }
  `,
})
export class NfMonacoEditorComponent implements AfterViewInit {
  readonly value = input<string>('{}');
  readonly language = input<string>('json');
  readonly height = input<string>('400px');
  readonly readOnly = input<boolean>(false);
  /** Monaco theme; `vs-dark` for dark mode. Monaco themes are global, see `cleanUp()`. */
  readonly theme = input<'vs' | 'vs-dark'>('vs');
  /** Whether JSON errors are reported (footer and `validChange`). Viewers pass `false`. */
  readonly validate = input<boolean>(true);

  readonly valueChange = output<string>();
  readonly validChange = output<boolean>();
  /**
   * A read-only editor reports Escape (when no find or suggest widget is open) because Monaco
   * consumes the key, which would otherwise keep a surrounding dialog from closing.
   */
  readonly escapePressed = output<void>();

  private readonly editorContainer = viewChild<ElementRef<HTMLDivElement>>('editorContainer');
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly hasError = signal(false);
  protected readonly errorsHint = translated('common.monaco.jsonErrors');
  /** True when Monaco failed to load; the value is then shown as plain text. */
  protected readonly loadFailed = signal(false);
  /** True until Monaco has created the editor; a skeleton is shown meanwhile. */
  protected readonly loading = signal(true);

  private editor: MonacoEditor.IStandaloneCodeEditor | null = null;
  private monacoInstance: Monaco | null = null;
  private readonly disposables: { dispose(): void }[] = [];

  constructor() {
    // Sync external value changes into the editor (e.g. when schema loads from Firestore).
    // Skip the update when the value was just emitted by the editor itself to avoid
    // resetting cursor position on every keystroke.
    effect(() => {
      const newValue = this.value();
      if (this.editor && this.editor.getValue() !== newValue) {
        this.editor.setValue(newValue);
      }
    });

    effect(() => {
      const theme = this.theme();
      this.monacoInstance?.editor.setTheme(theme);
    });

    this.destroyRef.onDestroy(() => this.cleanUp());
  }

  ngAfterViewInit(): void {
    this.initMonaco().catch((error: unknown) => {
      console.error('[NfMonacoEditorComponent] Failed to initialize Monaco:', error);
      this.loadFailed.set(true);
    });
  }

  private async initMonaco(): Promise<void> {
    const container = this.editorContainer()?.nativeElement;
    if (!container) return;

    const monaco = await loader.init();
    if (!container.isConnected) return;

    this.monacoInstance = monaco;

    // Enforce strict JSON (no comments, no trailing commas)
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      enableSchemaRequest: false,
      comments: 'error',
      trailingCommas: 'error',
    });

    const editor = this.ngZone.runOutsideAngular(() =>
      monaco.editor.create(container, {
        value: this.value(),
        language: this.language(),
        theme: this.theme(),
        minimap: { enabled: false },
        automaticLayout: true,
        scrollBeyondLastLine: false,
        lineNumbers: 'on',
        folding: true,
        tabSize: 2,
        fontSize: 13,
        fontFamily: '"Fira Code", "Cascadia Code", ui-monospace, monospace',
        readOnly: this.readOnly(),
        padding: { top: 8, bottom: 8 },
        renderLineHighlight: 'gutter',
        overviewRulerLanes: 0,
        renderValidationDecorations: this.validate() ? 'on' : 'off',
        // A read-only viewer must not trap keyboard users: Tab moves focus on.
        tabFocusMode: this.readOnly(),
      })
    );
    this.editor = editor;
    this.loading.set(false);

    if (this.readOnly()) {
      editor.addCommand(
        monaco.KeyCode.Escape,
        () => this.ngZone.run(() => this.escapePressed.emit()),
        '!findWidgetVisible && !suggestWidgetVisible'
      );
    }

    // Emit value on every content change
    this.disposables.push(
      editor.onDidChangeModelContent(() => {
        const val = editor.getValue();
        this.ngZone.run(() => this.valueChange.emit(val));
      })
    );

    // Emit validity whenever the JSON worker updates markers (async after content change)
    this.disposables.push(
      monaco.editor.onDidChangeMarkers((uris: readonly Uri[]) => {
        const model = editor.getModel();
        if (!model || !this.validate()) return;
        const belongsToThisEditor = uris.some((u: Uri) => u.toString() === model.uri.toString());
        if (!belongsToThisEditor) return;

        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        const hasErrors = markers.some(
          (m: MonacoEditor.IMarker) => m.severity === monaco.MarkerSeverity.Error
        );
        this.ngZone.run(() => {
          this.hasError.set(hasErrors);
          this.validChange.emit(!hasErrors);
        });
      })
    );
  }

  private cleanUp(): void {
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.editor?.dispose();
    this.editor = null;
    // Monaco's theme is global: a dark viewer must not leave a schema editor mounted behind it dark.
    if (this.theme() !== 'vs') this.monacoInstance?.editor.setTheme('vs');
    this.monacoInstance = null;
  }
}
